import type { Controller } from 'bernie-core';
import type { RenderRequest, Request } from 'bernie-http';
import type { ExperimentStore } from 'bernie-plugin-mobx';

import type { TemplateStore } from 'stores/template-store';

import type { TemplateName } from 'constants/template-name';

type FetchParams = Parameters<Controller['fetch']>;

/**
 * This abstract class enables controllers to easily enable templating. This class handles fetching
 * templates during the server side render and caching the results. This cache is persisted from the
 * server render to the client via the custom serializer in the template store. In order to enable
 * all of this, clients simply need to extend this class.
 *
 * @example
 *    class YourController extends AbstractTemplateController implements Controller {
 *      public readonly templateName = TemplateName.YOUR_TEMPLATE_NAME;
 *
 *      // this method is optional and defaults to returning true if not specified
 *      public isTemplatingEnabled(experimentStore: ExperimentStore): boolean {
 *        return experimentStore.exposures['TEMPLATE_EXPERIMENT']?.bucket === 1;
 *      }
 *
 *      ...
 *    }
 */
export abstract class AbstractTemplateController {
  protected abstract fetch(...params: FetchParams): ReturnType<Controller['fetch']>;
  public abstract templateName: TemplateName;
  public isTemplatingEnabled?(request: ExperimentStore): boolean;

  private baseControllerFetch: (...params: FetchParams) => ReturnType<Controller['fetch']>;

  constructor() {
    this.baseControllerFetch = this.fetch;

    this.fetch = async function (...params: FetchParams) {
      /**
       * We only want to fetch the template on the server render. Doing so will populate the template
       * cache on the server, pass that cached template to the browser, and then the browser will
       * utilize the useTemplate hook to retrieve the cached template (on the initial render). For
       * rendering via client side routing, the useTemplate hook will be used to fetch templates.
       */
      if (params[0].isServer) {
        /**
         * determines if we should call the template API and handles calling the API if necessary
         */
        await this.handleTemplateFetch(...params);
      }

      return await this.baseControllerFetch(...params);
    };
  }

  private async handleTemplateFetch(...params: FetchParams): Promise<void> {
    const { stores } = params[0];
    const shouldFetchTemplate = this.isTemplatingEnabled?.(stores.get<ExperimentStore>('experiment')) ?? true;

    if (shouldFetchTemplate) {
      const templateStore = stores.get<TemplateStore>('template');
      const { request } = params[0];

      const uri = this.isRenderRequest(request) ? request.serverRequest.parsedUrl.href : undefined;
      await templateStore.fetchTemplate(this.templateName, uri);
    }
  }

  private isRenderRequest(request: Request): request is RenderRequest {
    return (request as RenderRequest).serverRequest != null;
  }
}
